import type { IntlShape } from 'react-intl';

import { Match } from 'effect';

import { ErrorTag } from '@vs-shop/engine/data/error/index';
import type { OrderViolationError } from '@vs-shop/engine/data/error/orderViolation';

export const getViolationErrorMessage = (
  violation: OrderViolationError,
  intl: IntlShape
): string => {
  // Prefer violation.message from API if available
  if (violation.message) {
    return violation.message;
  }

  // Fallback to translated messages by violation type
  return Match.value(violation).pipe(
    Match.tag(ErrorTag.PaymentMethodEligibilityError, () =>
      intl.formatMessage({
        id: 'error.orderViolation.paymentMethodEligibility',
        defaultMessage: 'Tato platební metoda byla zakázána. Prosím vyberte jinou platební metodu.',
      })
    ),
    Match.tag(ErrorTag.ProductEligibilityError, () =>
      intl.formatMessage({
        id: 'error.orderViolation.productEligibility',
        defaultMessage: 'Tento produkt byl zakázán.',
      })
    ),
    Match.tag(ErrorTag.ShippingMethodEligibilityError, () =>
      intl.formatMessage({
        id: 'error.orderViolation.shippingMethodEligibility',
        defaultMessage: 'Produkt nesplňuje požadavky pro dopravní metodu.',
      })
    ),
    Match.tag(ErrorTag.ShippingMethodNotAvailableError, () =>
      intl.formatMessage({
        id: 'error.orderViolation.shippingMethodNotAvailable',
        defaultMessage: 'Dopravní metoda není k dispozici. Prosím vyberte jinou dopravní metodu.',
      })
    ),
    Match.tag(ErrorTag.AddressRequirementError, () =>
      intl.formatMessage({
        id: 'error.orderViolation.addressRequirement',
        defaultMessage: 'Prosím zadejte adresu.',
      })
    ),
    Match.tag(ErrorTag.CurrencyCodeNotValidError, () =>
      intl.formatMessage({
        id: 'error.orderViolation.currencyCodeNotValid',
        defaultMessage: 'Zadaný kód měny je neplatný.',
      })
    ),
    Match.tag(ErrorTag.CartItemNotAvailableError, () =>
      intl.formatMessage({
        id: 'error.orderViolation.cartItemNotAvailable',
        defaultMessage: 'Produkt nemá dostatečný sklad.',
      })
    ),
    Match.tag(ErrorTag.CartItemInsufficientStockError, () =>
      intl.formatMessage({
        id: 'error.orderViolation.cartItemInsufficientStock',
        defaultMessage: 'Nedostatečný sklad.',
      })
    ),
    Match.tag(ErrorTag.CartItemQuantityNotInRangeError, () =>
      intl.formatMessage({
        id: 'error.orderViolation.cartItemQuantityNotInRange',
        defaultMessage: 'Nedostatečný sklad.',
      })
    ),
    Match.tag(ErrorTag.CartItemVariantNotAvailableError, () =>
      intl.formatMessage({
        id: 'error.orderViolation.cartItemVariantNotAvailable',
        defaultMessage: 'Produkt není k dispozici.',
      })
    ),
    Match.tag(ErrorTag.OrderNotEmptyError, () =>
      intl.formatMessage({
        id: 'error.orderViolation.orderNotEmpty',
        defaultMessage: 'Prázdnou objednávku nelze zpracovat.',
      })
    ),
    Match.tag(ErrorTag.ProductVariantCodeNotExistError, () =>
      intl.formatMessage({
        id: 'error.orderViolation.productVariantCodeNotExist',
        defaultMessage: 'Produkt neexistuje.',
      })
    ),
    Match.tag(ErrorTag.ProductVariantNotExistError, () =>
      intl.formatMessage({
        id: 'error.orderViolation.productVariantNotExist',
        defaultMessage: 'Produkt neexistuje.',
      })
    ),
    Match.tag(ErrorTag.ProductVariantNotSufficientError, () =>
      intl.formatMessage({
        id: 'error.orderViolation.productVariantNotSufficient',
        defaultMessage: 'Nedostatečný sklad.',
      })
    ),
    Match.tag(ErrorTag.OrderInvalidStateTransitionError, () =>
      intl.formatMessage({
        id: 'error.orderViolation.orderInvalidStateTransition',
        defaultMessage:
          'Nelze dokončit, protože objednávka je v nesprávném stavu. Objednávka bude obnovena.',
      })
    ),
    Match.tag(ErrorTag.OrderCartItemsImmutableError, () =>
      intl.formatMessage({
        id: 'error.orderViolation.orderCartItemsImmutable',
        defaultMessage:
          'Po dokončení objednávky nelze změnit položky v košíku. Objednávka bude obnovena.',
      })
    ),
    Match.tag(ErrorTag.UnknownViolationError, (error) =>
      intl.formatMessage(
        {
          id: 'error.orderViolation.unknown',
          defaultMessage: 'Neznámá chyba validace: {code}',
        },
        { code: error.code }
      )
    ),
    Match.exhaustive
  );
};
